import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const LAST_UPDATED = 'April 18, 2026';
const CONTACT_EMAIL = 'privacy@selahandstonelegacy.com';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="home-link">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/dashboard"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 animate-fade-in-up">
          <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-2">
            Legal
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold text-[#36454F]">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-[#36454F]/60 font-['Manrope']">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <article className="bg-white border border-[#36454F]/10 rounded-sm p-6 sm:p-10 space-y-8 font-['Manrope'] text-[#36454F]/85 leading-relaxed">
          <Section title="Our Commitment">
            Selah Slate is a faith-based financial wellness tool provided free of charge by
            Selah and Stone Legacy Holdings LLC ("Selah Slate," "we," "us," or "our"). We are
            not a credit repair clinic, law firm, or financial institution. This Privacy Policy
            explains what information we collect, how we use it, how we protect it, and the
            rights you have over it.
            {' '}
            <strong>We do not sell your personal information. Ever.</strong>
          </Section>

          <Section title="1. Information We Collect">
            <p>We collect only what is necessary to help you generate credit dispute letters:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Account information</strong>: Your email address, first and last name, and a hashed password. If you sign in with Google, we receive your name, email, and profile picture only.</li>
              <li><strong>Dispute information</strong>: Your mailing address (current and optional previous), the last 4 digits of your SSN (encrypted at rest), and the accounts / items you choose to dispute.</li>
              <li><strong>Credit report uploads</strong>: PDFs you upload from AnnualCreditReport.com or any bureau. We extract the text, analyze it for negative items, and then <strong>permanently delete the extracted text</strong>. The original file is not retained beyond analysis.</li>
              <li><strong>Dispute activity</strong>: The letters you generate, the dates they are mailed, bureau responses you log, and follow-up actions.</li>
              <li><strong>Optional content</strong>: Journal entries, testimonies, debt calculator inputs, and prayer/strategy preferences you create.</li>
              <li><strong>Device and usage data</strong>: Limited analytics (page views, errors, performance) — no cross-app tracking, no advertising identifiers, no location tracking.</li>
            </ul>
            <p className="mt-3">
              <strong>What we do NOT collect</strong>: your full Social Security number, your bank
              account numbers, your credit card numbers, your date of birth, or your credit score.
            </p>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-2">
              <li>To generate your FCRA Section 611 dispute letters and related PDFs.</li>
              <li>To track your 35-day response window and suggest next-step actions (e.g., MOV letters, CFPB complaints).</li>
              <li>To send essential account emails (welcome, password reset, dispute milestones) — you can disable non-essential emails in Settings.</li>
              <li>To display aggregate, anonymized progress in the optional Ministry Dashboard (no personal information is shared).</li>
              <li>To protect the service from fraud and abuse.</li>
            </ul>
            <p className="mt-3">
              We never use your information for advertising. We do not show ads, and we do not
              sell or rent your information to third parties.
            </p>
          </Section>

          <Section title="3. AI Processing of Credit Reports">
            <p>
              To identify negative items in your uploaded report, we send the extracted text to a
              third-party AI provider (currently OpenAI) using an automated, secure channel. The
              text is processed to return structured data (item name, issue type, balance,
              reporting bureau) and is <strong>not used to train any AI model</strong>. Selah
              Slate deletes the raw text from our servers immediately after analysis completes.
            </p>
          </Section>

          <Section title="4. How We Protect Your Information">
            <ul className="list-disc pl-6 space-y-2">
              <li>Passwords are hashed using industry-standard bcrypt; we cannot see your password.</li>
              <li>The last 4 digits of your SSN are encrypted at rest.</li>
              <li>All data is transmitted over HTTPS / TLS.</li>
              <li>Uploaded PDF text is purged immediately after AI analysis.</li>
              <li>Access to production systems is restricted and logged.</li>
            </ul>
          </Section>

          <Section title="5. Third Parties We Use">
            <p>We rely on a small number of trusted service providers, each bound by their own security terms:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>OpenAI</strong> — AI-powered credit report analysis. Text is sent under a no-training agreement.</li>
              <li><strong>Resend</strong> — Transactional email delivery (welcome, password reset, dispute reminders).</li>
              <li><strong>MongoDB</strong> — Encrypted database hosting.</li>
              <li><strong>Google Sign-In</strong> (optional) — Authentication only; we never post to or read your Google data beyond basic profile.</li>
              <li><strong>PostHog</strong> — Anonymous product analytics; no personally identifying data is sent.</li>
            </ul>
          </Section>

          <Section title="6. Your Rights & Choices">
            <p>You are in control of your data at all times.</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Access</strong>: View all of your account info from the Profile page.</li>
              <li><strong>Correction</strong>: Update your name, address, or SSN digits at any time.</li>
              <li><strong>Export</strong>: Email <a className="text-[#B76E79] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will send you a machine-readable copy within 30 days.</li>
              <li><strong>Deletion</strong>: Delete your account and all associated data instantly from Settings → Delete Account. Once you confirm, all disputes, letters, journal entries, and testimonies tied to your account are permanently erased.</li>
              <li><strong>Marketing emails</strong>: Toggle welcome, reminder, and celebration emails in Settings. Transactional security emails (password reset) cannot be disabled.</li>
              <li><strong>California (CCPA) / Virginia (VCDPA) / EU (GDPR) rights</strong>: You have the right to know, correct, delete, and — where applicable — port your data. Contact <a className="text-[#B76E79] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to exercise any of these rights.</li>
              <li><strong>Do Not Sell</strong>: We do not and will not sell your personal information, so nothing to opt out of.</li>
            </ul>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain your account information while your account is active. Generated dispute
              letters are retained so you can re-download them (we recommend saving your own
              copies). Raw extracted PDF text is deleted within minutes of analysis. When you
              delete your account, all of the above is permanently removed from our production
              database within 30 days, except where we are required by law to retain limited
              records (e.g., fraud-prevention logs).
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              Selah Slate is intended for individuals 18 years of age or older. We do not
              knowingly collect personal information from children under 13. If you believe a
              child has provided information, please contact us and we will delete it.
            </p>
          </Section>

          <Section title="9. Tracking / Do Not Track">
            <p>
              Selah Slate does not display advertising, does not use cross-app tracking, and does
              not use the IDFA or Android Advertising ID. Our app's App Store and Google Play
              listings accurately reflect that no data is "linked to you for tracking purposes."
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update
              the "Last updated" date above and, for any material change, notify you by email or
              in-app banner at least 14 days before it takes effect.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#B76E79]" strokeWidth={1.5} />
              <a className="text-[#B76E79] underline" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="text-sm text-[#36454F]/60 mt-3">
              Selah and Stone Legacy Holdings LLC — Attn: Privacy<br />
              Selah Slate™ is a financial wellness initiative. We respond to privacy requests
              within 30 days.
            </p>
          </Section>
        </article>

        <p className="text-xs text-[#36454F]/45 font-['Manrope'] text-center mt-6">
          You can also review our{' '}
          <Link to="/terms" className="text-[#B76E79] hover:underline" data-testid="terms-link">
            Terms and Conditions
          </Link>
          .
        </p>
      </main>

      <AppFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-3">
        {title}
      </h2>
      <div className="text-sm space-y-2">{children}</div>
    </section>
  );
}
